import { useQuery } from '@tanstack/preact-query';
import { useContext, useState } from 'preact/hooks';
import { ApiClientContext } from '../arcticapi';
import type { ArcticApiClient } from '../arcticapi';
import type {
  Disruption,
  JourneyPlan,
  JourneyLeg,
  JourneyPlanPageLink,
  JourneyPlanRequest,
  JourneyPlanResponse,
} from '../arcticapi/types';

export interface JourneyPlanPage {
  title?: string;
  time?: string;
}

export interface UseJourneyPlanResult {
  submitJourneyPlan: (request: JourneyPlanRequest) => void;
  fetchEarlier: () => boolean;
  fetchLater: () => boolean;
  plans: JourneyPlan[];
  earlierPage: JourneyPlanPage | null;
  laterPage: JourneyPlanPage | null;
  request: JourneyPlanRequest | null;
  loading: boolean;
  error: Error | null;
  hasSubmitted: boolean;
}

// Represents the source of a journey plan fetch, either from a direct request or an API pagination link
type JourneyPlanSubmission =
  // Form-driven search from origin/destination inputs.
  | { type: 'request'; request: JourneyPlanRequest }
  // Link-driven search from API pagination links (earlier/later).
  | { type: 'href'; href: string };

/**
 * Executes a single journey-plan API request.
 *
 * A "journey plan" is the concrete result returned by `/plan`
 * for one origin/destination coordinate pair.
 */
export function useJourneyPlan(): UseJourneyPlanResult {
  const client = useContext(ApiClientContext);
  const [submission, setSubmission] = useState<JourneyPlanSubmission | null>(
    null,
  );

  const query = useQuery<JourneyPlanResponse, Error>({
    queryKey: buildJourneyPlanQueryKey(submission),
    enabled: submission !== null,
    queryFn: async ({ signal }) => {
      if (!submission) {
        throw new Error('Journey planning request is missing.');
      }

      if (!client) {
        throw new Error(
          'Journey planning is unavailable in this preview context.',
        );
      }

      return fetchAndEnrichJourneyPlanResponse(submission, client, signal);
    },
  });

  const hasSubmitted = submission !== null;
  const request = submission?.type === 'request' ? submission.request : null;
  const earlierPage = toJourneyPlanPage(query.data?.earlierPage);
  const laterPage = toJourneyPlanPage(query.data?.laterPage);

  const submitJourneyPlanPage = (page: JourneyPlanPageLink) => {
    setSubmission({ type: 'href', href: page.href });
  };

  const fetchEarlier = () => {
    if (!query.data?.earlierPage) {
      return false;
    }

    submitJourneyPlanPage(query.data.earlierPage);
    return true;
  };

  const fetchLater = () => {
    if (!query.data?.laterPage) {
      return false;
    }

    submitJourneyPlanPage(query.data.laterPage);
    return true;
  };

  return {
    submitJourneyPlan: (nextRequest) =>
      setSubmission({ type: 'request', request: nextRequest }),
    fetchEarlier,
    fetchLater,
    plans: query.data?.plans ?? [],
    earlierPage,
    laterPage,
    request,
    loading: hasSubmitted && query.isPending,
    error: query.error,
    hasSubmitted,
  };
}

function buildJourneyPlanQueryKey(
  submission: JourneyPlanSubmission | null,
): unknown[] {
  if (!submission) {
    return ['journey-plan', null];
  }

  if (submission.type === 'request') {
    return ['journey-plan', submission.request];
  }

  return ['journey-plan', { href: submission.href }];
}

function toJourneyPlanPage(
  page: JourneyPlanPageLink | undefined,
): JourneyPlanPage | null {
  if (!page) {
    return null;
  }

  return {
    title: page.title,
    time: page.time,
  };
}

/**
 * Handle submitting a journey plan request, either from a new origin/destination search or from links.
 */
async function fetchAndEnrichJourneyPlanResponse(
  submission: JourneyPlanSubmission,
  client: ArcticApiClient,
  signal: AbortSignal,
): Promise<JourneyPlanResponse> {
  // Shared fetch path for both request-based and href-based journey searches.
  const response =
    submission.type === 'request'
      ? await client.planJourney(submission.request, { signal })
      : await client.planJourneyByHref(submission.href, { signal });

  const stopAlertsByHref = await fetchStopAlertsByHref(
    response.plans,
    client,
    signal,
  );

  return {
    ...response,
    plans: response.plans.map((plan) => ({
      ...plan,
      legs: plan.legs.map((leg) =>
        enrichJourneyLegStopsWithAlerts(leg, stopAlertsByHref),
      ),
    })),
  };
}

async function fetchStopAlertsByHref(
  plans: JourneyPlan[],
  client: ArcticApiClient,
  signal: AbortSignal,
): Promise<Map<string, Disruption[]>> {
  const uniqueAlertHrefs = new Set<string>();

  for (const plan of plans) {
    for (const leg of plan.legs) {
      for (const stop of leg.stops ?? []) {
        const alertsHref = stop.links?.alerts;

        if (alertsHref) {
          uniqueAlertHrefs.add(alertsHref);
        }
      }
    }
  }

  const alertEntries = await Promise.all(
    [...uniqueAlertHrefs].map(
      async (alertsHref) =>
        [
          alertsHref,
          await client.getDisruptions(alertsHref, { signal }),
        ] as const,
    ),
  );

  return new Map(alertEntries);
}

function enrichJourneyLegStopsWithAlerts(
  leg: JourneyLeg,
  stopAlertsByHref: Map<string, Disruption[]>,
): JourneyLeg {
  if (!leg.stops?.length) {
    return leg;
  }

  return {
    ...leg,
    stops: leg.stops.map((stop) => ({
      ...stop,
      alerts: stop.links?.alerts
        ? (stopAlertsByHref.get(stop.links.alerts) ?? [])
        : stop.alerts,
    })),
  };
}
